import { roundPercentages } from './percentages.js';
const SEASON_LABEL = '2025/26';
const BRAND_SUFFIX = ' | CrowdFPL';
const TITLE_MAX = 60;
const DESC_MAX = 155;
const DEFAULT_ORIGIN = 'https://crowdfpl.com';
export function computeDuelVoteSplit(aVotes, bVotes, totalVotes) {
    const total = totalVotes ?? aVotes + bVotes;
    const [aPct, bPct] = roundPercentages(aVotes, bVotes);
    const favored = total === 0 || aPct === bPct ? null : aPct > bPct ? 'a' : 'b';
    return { totalVotes: total, aVotes, bVotes, aPct, bPct, favored };
}
export function computeDuelVoteSplitFromVotes(votes) {
    const totalVotes = votes.total_votes ?? 0;
    const aVotes = votes.player_a_votes ?? 0;
    const bVotes = votes.player_b_votes ?? 0;
    return computeDuelVoteSplit(aVotes, bVotes, totalVotes);
}
export function buildDuelH1(paShort, pbShort, gw) {
    return `${paShort} vs ${pbShort} — FPL Gameweek ${gw} Crowd Vote`;
}
/** Core title for client Helmet (brand suffix appended separately). */
export function buildDuelTitleCore(paShort, pbShort, gw) {
    const variants = [
        `${paShort} vs ${pbShort} FPL — GW${gw} ${SEASON_LABEL} Crowd Pick`,
        `${paShort} vs ${pbShort} FPL — GW${gw} Crowd Pick`,
        `${paShort} vs ${pbShort} — GW${gw} Crowd Pick`,
        `${paShort} vs ${pbShort} GW${gw}`,
    ];
    const maxCoreLen = TITLE_MAX - BRAND_SUFFIX.length;
    for (const candidate of variants) {
        if (candidate.length <= maxCoreLen) {
            return candidate;
        }
    }
    return variants[variants.length - 1].slice(0, maxCoreLen);
}
export function buildDuelPageTitle(paShort, pbShort, gw) {
    const core = buildDuelTitleCore(paShort, pbShort, gw);
    const full = `${core}${BRAND_SUFFIX}`;
    return full.length <= TITLE_MAX ? full : core.slice(0, TITLE_MAX);
}
export function buildDuelDescription(paShort, pbShort, gw, voteSplit) {
    const { totalVotes, aPct, bPct, favored } = voteSplit;
    const variants = totalVotes > 0 && favored
        ? (() => {
            const winner = favored === 'a' ? paShort : pbShort;
            const loser = favored === 'a' ? pbShort : paShort;
            const winnerPct = favored === 'a' ? aPct : bPct;
            return [
                `${winnerPct}% pick ${winner} over ${loser} for GW${gw}. Should you pick ${paShort} or ${pbShort}? Compare FPL stats, ownership and form.`,
                `${winnerPct}% of managers pick ${winner} for GW${gw}. Pick ${paShort} or ${pbShort}? Compare stats, ownership and form on CrowdFPL.`,
                `Should you pick ${paShort} or ${pbShort}? ${winnerPct}% crowd vote for GW${gw}. Compare FPL stats and ownership.`,
            ];
        })()
        : [
            `Should you pick ${paShort} or ${pbShort} for GW${gw}? Compare FPL stats, ownership and form on CrowdFPL.`,
            `Pick ${paShort} or ${pbShort} for GW${gw}? Compare stats, ownership and form — cast the first crowd vote.`,
        ];
    for (const candidate of variants) {
        if (candidate.length <= DESC_MAX && /or/i.test(candidate) && /stats/i.test(candidate)) {
            return candidate;
        }
    }
    const fallback = variants[0];
    return fallback.length <= DESC_MAX ? fallback : `${fallback.slice(0, DESC_MAX - 3)}...`;
}
export function buildDuelFaqQuestion(paShort, pbShort) {
    return `Should I pick ${paShort} or ${pbShort}?`;
}
export function buildDuelFaqAnswer(paShort, pbShort, gw, voteSplit) {
    const { totalVotes, aPct, bPct, favored } = voteSplit;
    if (totalVotes > 0 && favored) {
        const winner = favored === 'a' ? paShort : pbShort;
        const winnerPct = favored === 'a' ? aPct : bPct;
        return `${winnerPct}% of CrowdFPL managers pick ${winner}. Based on ${totalVotes} votes as of GW${gw}.`;
    }
    return `Compare ${paShort} and ${pbShort} for GW${gw} and cast the first vote on CrowdFPL.`;
}
function buildVoteDescription(paShort, pbShort, gw, voteSplit) {
    const { totalVotes, aPct, bPct, favored } = voteSplit;
    if (totalVotes > 0 && favored) {
        const winner = favored === 'a' ? paShort : pbShort;
        const loser = favored === 'a' ? pbShort : paShort;
        const winnerPct = favored === 'a' ? aPct : bPct;
        return `${winnerPct}% of CrowdFPL managers pick ${winner} over ${loser} for GW${gw} (${totalVotes} votes).`;
    }
    return `Crowd vote for ${paShort} vs ${pbShort} in GW${gw} — no votes yet.`;
}
export function buildDuelCanonicalUrl(origin, slug) {
    const base = origin || DEFAULT_ORIGIN;
    return `${base}/duel/${slug}`;
}
export function buildDuelJsonLdGraph(input) {
    const { paShort, pbShort, paName, pbName, gw, slug, origin = DEFAULT_ORIGIN, deadlineTime, voteSplit, } = input;
    const base = origin || DEFAULT_ORIGIN;
    const canonicalUrl = buildDuelCanonicalUrl(base, slug);
    const faqQuestion = buildDuelFaqQuestion(paShort, pbShort);
    const faqAnswer = buildDuelFaqAnswer(paShort, pbShort, gw, voteSplit);
    const h1Title = buildDuelH1(paShort, pbShort, gw);
    const voteDescription = buildVoteDescription(paShort, pbShort, gw, voteSplit);
    // location + OnlineEventAttendanceMode required by Google Event rich results
    const sportsEvent = {
        '@type': 'SportsEvent',
        name: h1Title,
        description: `CrowdFPL FPL duel: ${paShort} vs ${pbShort} in Gameweek ${gw}. ${voteDescription}`,
        disambiguatingDescription: voteDescription,
        eventStatus: 'https://schema.org/EventScheduled',
        eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
        sport: 'Fantasy Premier League',
        url: canonicalUrl,
        inLanguage: 'en',
        location: { '@type': 'VirtualLocation', url: canonicalUrl },
        competitor: [
            { '@type': 'Person', name: paName },
            { '@type': 'Person', name: pbName },
        ],
    };
    if (deadlineTime) {
        sportsEvent.startDate = deadlineTime;
    }
    return {
        '@context': 'https://schema.org',
        '@graph': [
            sportsEvent,
            {
                '@type': 'FAQPage',
                mainEntity: [
                    {
                        '@type': 'Question',
                        name: faqQuestion,
                        acceptedAnswer: {
                            '@type': 'Answer',
                            text: faqAnswer,
                        },
                    },
                ],
            },
            {
                '@type': 'BreadcrumbList',
                itemListElement: [
                    { '@type': 'ListItem', position: 1, name: 'Home', item: `${base}/` },
                    { '@type': 'ListItem', position: 2, name: 'Duels', item: `${base}/duels` },
                    { '@type': 'ListItem', position: 3, name: `${paShort} vs ${pbShort} GW${gw}` },
                ],
            },
        ],
    };
}
